<template>
  <header class="b2b-header" :class="{
    'b2b-header--top': scrollTop,
  }">
    <template v-if="!loading">
      <div class="b2b-header__toolbar" v-if="!loading">
        <div class="b2b-header__start">
          <b2b-button-icon v-if="$_backButton" :to="$_backButton">
            <arrow-left-icon />
          </b2b-button-icon>

          <router-link v-if="!$_isMobile || !$_backButton" to="/redirect" class="ml--3">
            <b2b-flex flex="0" align="center" row>
              <template v-if="$_company.logo">
                <b2b-image
                  class="b2b-header__logo"
                  contain
                  :src="$_company.logo"
                  :alt="$_company.tradingName"
                  :style="$_companyLogoStyle" />
              </template>

              <h1 v-if="!$_company.logo" class="b2b-header__company"
                v-text="$_company.tradingName" />
            </b2b-flex>
          </router-link>
        </div>

        <div v-if="$_isCustomer" class="b2b-header__search">
          <b2b-tooltip
            v-if="$_isCustomer"
            :label="$t('labels.filters')"
          >
            <template #activator>
              <div class="b2b-header__cart">
                <b2b-button-icon
                  v-if="$_isCustomer"
                  :activeButton="tags.length > 0"
                  @click="$_onClickFilterTags"
                >
                  <filter-icon />
                </b2b-button-icon>
              </div>
            </template>
          </b2b-tooltip>

          <b2b-input-search
            v-model="search"
            clearable
            bordered
            flat
            class="b2b-header__search-input"
            @input="$_onSearchInput"
          />
        </div>

        <b2b-flex flex="0" align="center" row class="b2b-header__actions">
          <b2b-tooltip
            v-if="$_isCustomer"
            :label="$t('resources.menu.tooltip.cart')"
          >
            <template #activator>
              <div class="b2b-header__cart">
                <div v-if="!$_exibirPreco" class="b2b-header__cart-info" v-text="$_cartInfo" />

                <b2b-button-icon
                  v-if="$_isCustomer"
                  :to="{ name: 'ShoppingCart' }"
                  :activeButton="$_cartExistItem"
                >
                  <shopping-cart-icon />
                </b2b-button-icon>
              </div>
            </template>
          </b2b-tooltip>

          <b2b-bell-menu class="ml--2" v-if="$_isCustomer" />

          <b2b-tooltip
            v-if="$_isCustomer"
            :label="$t('resources.menu.favorites')">
            <template #activator>
              <b2b-button-icon
                v-if="$_isCustomer"
                :to="{ name: 'FavoritesIndex' }"
                class="ml--2"
              >
                <heart-icon />
              </b2b-button-icon>
            </template>
          </b2b-tooltip>

          <b2b-profile-menu class="ml--2" />
        </b2b-flex>
      </div>

      <div>
        <slot :scrollTop="scrollTop" />
      </div>

      <b2b-filter-tags
        ref="filterTags"
        v-if="$_isCustomer"
        :value="tags"
        :active.sync="filterTagsActive"
        hide-activator
        @input="$_onTagsInput"
      />
    </template>
  </header>
</template>

<script>
import { ArrowLeftIcon, ShoppingCartIcon, HeartIcon, FilterIcon } from 'vue-feather-icons';

import ActionResolver from '../../mixins/action-resolver';
import BreakpointMixin from '../../mixins/base/breakpoints-mixin';
import WindowVisibilityResolver from '../../mixins/window-visibility-resolver';

import B2bFlex from '../core/b2b-flex.vue';
import B2bButtonIcon from '../core/buttons/b2b-button-icon.vue';
import B2bProfileMenu from './b2b-profile-menu.vue';
import B2bImage from '../core/b2b-image.vue';
import B2bBellMenu from './b2b-bell-menu.vue';
import B2bTooltip from '../core/b2b-tooltip.vue';
import B2bInputSearch from '../core/form/b2b-input-search.vue';
import B2bFilterTags from '../filters/b2b-filter-tags.vue';

export default {
  name: 'b2b-header',

  components: {
    B2bFlex,
    B2bButtonIcon,
    ArrowLeftIcon,
    ShoppingCartIcon,
    HeartIcon,
    FilterIcon,
    B2bProfileMenu,
    B2bImage,
    B2bBellMenu,
    B2bTooltip,
    B2bInputSearch,
    B2bFilterTags,
  },

  mixins: [
    ActionResolver(),

    BreakpointMixin(),

    WindowVisibilityResolver({
      method: '$_onWindowVisibilityChange',
    }),
  ],

  props: {
    loading: Boolean,
  },

  data() {
    return {
      filterTagsActive: false,
      search: null,
      tags: [],
      scrollTop: true,
    };
  },

  computed: {
    $_exibirPreco() {
      return Boolean(this.$store.getters['session/company'].esconderPreco) && this.$store.getters['session/isCustomer'];
    },

    $_exibirEstoque() {
      return Boolean(this.$store.getters['session/company'].obrigaEstoque) && this.$store.getters['session/isCustomer'];
    },

    $_cartExistItem() {
      if (this.$_exibirPreco) {
        return Boolean(this.$store.getters['shopping-cart/total']);
      }

      return false;
    },

    $_company() {
      return this.$store.getters['session/company'] || {};
    },

    $_companyLogoStyle() {
      const primaryColor = this.$_company.primaryColor || 'var(--main-color--primary)';

      return {
        borderColor: primaryColor,
        backgroundColor: `${primaryColor}12`,
      };
    },

    $_customer() {
      return this.$store.getters['session/customer'];
    },

    $_isCustomer() {
      return this.$store.getters['session/isCustomer'];
    },

    $_cartInfo() {
      const total = this.$store.getters['shopping-cart/total'];
      return this.$n(total || 0, 'currency');
    },

    $_backButton() {
      return this.$route.query?.back ?? this.$route.meta?.config?.back;
    },
  },

  methods: {
    $_parseTags(value) {
      const tags = String(value || '').trim();
      return !tags ? [] : tags.split(',').filter((item) => !!item);
    },

    $_syncTagsFromRoute() {
      this.tags = this.$_parseTags(this.$route.query?.t);
    },

    $_loadTotals() {
      if (!this.$_customer || this.loading) return;

      this.$nextTick(() => {
        this.$_actionResolver({
          action: 'shopping-cart/totals',
        });
      });
    },

    $_onWindowVisibilityChange($visible) {
      if (!$visible) return;

      this.$_loadTotals();
    },

    $_onSearchInput(search) {
      const nextSearch = search || undefined;
      const currentSearch = this.$route.query?.s || undefined;

      if (currentSearch === nextSearch && this.$route.name === 'Main') return;

      if (this.$route.name === 'Main') {
        this.$router.push({
          ...this.$route,
          query: {
            ...this.$route.query,
            p: undefined,
            s: nextSearch,
          },
        });

        return;
      }

      this.$router.push({
        name: 'Main',
        query: {
          s: nextSearch,
        },
      });
    },

    $_onClickFilterTags() {
      this.filterTagsActive = true;

      this.$nextTick(() => {
        this.$refs.filterTags?.$_ensureTagsLoaded();
      });
    },

    $_updateRouteTags(tags) {
      const nextTags = Array.isArray(tags) && tags.length > 0 ? tags.join(',') : undefined;
      const currentTags = this.$route.query?.t || undefined;

      if (currentTags === nextTags && this.$route.name === 'Main') return;

      if (this.$route.name !== 'Main') {
        this.$router.push({
          name: 'Main',
          query: {
            t: nextTags,
          },
        });

        return;
      }

      this.$router.push({
        ...this.$route,
        query: {
          ...this.$route.query,
          p: undefined,
          t: nextTags,
        },
      });
    },

    $_onTagsInput(tags) {
      this.tags = tags;
      this.$_updateRouteTags(tags);
    },
  },

  created() {
    this.search = this.$route.query?.s || null;
    this.$_syncTagsFromRoute();
    this.scrollTop = document.body.scrollTop === 0;

    window.onscroll = () => {
      this.scrollTop = (document.body.scrollTop || document.documentElement.scrollTop) === 0;
    };
  },

  watch: {
    '$route.query.s': {
      handler(value) {
        this.search = value || null;
      },
    },

    '$route.query.t': {
      handler() {
        this.$_syncTagsFromRoute();
      },
    },

    $_customer() {
      this.$_loadTotals();
    },

    loading() {
      this.$_loadTotals();
    },
  },
};
</script>

<style lang="scss">
@import '../../styles/break-points.scss';

$logoSize: 48px;

.b2b-header {
  z-index: 10;

  top: 0;
  width: 100%;
  height: 65px;
  position: sticky;
  background-color: $colorBackgroundDefault;
  transition: box-shadow 280ms cubic-bezier(0.4, 0, 0.2, 1);

  box-shadow:
    0px 2px 4px -1px rgba(0, 0, 0, 0.2),
    0px 4px 5px 0px rgba(0, 0, 0, 0.14),
    0px 1px 10px 0px rgba(0, 0, 0, 0.12);

  &--top {
    box-shadow: none;
    background-color: transparent;
  }

  &__toolbar {
    gap: 16px;
    display: flex;
    align-items: center;

    padding: 0 16px;
    height: $headerHeight - 24px;
    line-height: $headerHeight - 24px;
  }

  &__start {
    flex: 0 1 auto;
    min-width: 0;

    display: flex;
    align-items: center;
  }

  &__logo {
    margin-right: 16px;
    width: 50px;
    height: 50px;
    max-height: 55px;
    max-width: 55px;
    padding: 6px 10px;
    border: 2px solid var(--main-color--primary);
    border-radius: 30px;
    overflow: hidden;
    box-sizing: border-box;
    background-clip: padding-box;
  }

  &__company {
    display: none;
  }

  &__search {
    flex: 1 1 auto;
    min-width: 0;
    width: auto;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__search-input {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__filter {
    flex: 0 0 auto;
  }

  &__actions {
    flex: 0 0 auto;
    min-width: max-content;
    margin-left: auto;
  }

  &__cart {
    display: flex;
    align-items: center;
    min-width: max-content;
  }

  &__cart-info {
    height: 24px;
    line-height: 24px;
    padding: 0 8px;
    white-space: nowrap;
    margin-right: -3px;
    border-radius: 12px 0 0 12px;
  }

  .b2b-tooltip {
    &__content {
      top: 50%;
      left: calc(100% + 4px) !important;
      right: auto;
      transform: translateY(-50%) scale(0);
      transform-origin: left center;
    }

    &:hover > .b2b-tooltip__content {
      top: 50%;
      transform: translateY(-50%) scale(1);
    }
  }

  @include breakpointMax('tablet') {
    height: 124px;

    &__toolbar {
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
      height: auto;
      min-height: $headerHeight - 24px;
      padding-top: 8px;
      padding-bottom: 8px;
      line-height: normal;
    }

    &__start {
      flex: 1 1 auto;
      min-width: 0;
    }

    &__search {
      order: 3;
      flex: 1 0 100%;
      width: 100%;
      max-width: none;
      gap: 6px;
    }

    &__actions {
      flex: 0 0 auto;
    }

    &__logo {
      margin-right: 0;
      padding: 5px 8px;
    }

    &__cart-info {
      display: none;
    }
  }

  @media (max-width: 480px) {
    &__toolbar {
      gap: 6px;
      padding-left: 12px;
      padding-right: 12px;
    }

    &__logo {
      width: 50px;
      padding: 4px 6px;
    }
  }

  @include breakpointMin('laptop') {
    &__company {
      display: inline-block;
    }
  }
}
</style>
